import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  RequestUserMapping,
  SuccessResponseResponseUserMapping,
} from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import {
  DEFAULT_PAGE,
  MIN_SEARCH_LENGTH,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import { USER_MAPPING_LIST_QUERY_KEY, UserMappingSource } from './constants'
import { userMappingApi } from './services'

const QUERY_KEY_USER_MAPPING_LIST = 'user-mapping-list'

export const useUserMappingList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  source?: UserMappingSource
  enabled?: boolean
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    source,
    enabled = true,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '-created_at'
  )
  return useQuery({
    queryKey: USER_MAPPING_LIST_QUERY_KEY([query, page, rowsPerPage, sortBy]),
    queryFn: async () => {
      const { data, pagination }: SuccessResponseResponseUserMapping =
        await userMappingApi.getListUserMappings({
          q: query,
          offset: page * rowsPerPage,
          limit: rowsPerPage,
          sort_by: sortBy,
          source,
        })
      return { data, pagination }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  })
}

export const useCreateUserMapping = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: Partial<RequestUserMapping>) =>
      userMappingApi.createUserMapping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_USER_MAPPING_LIST] })
    },
  })
}

export const useUpdateUserMapping = (mappingId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: Partial<RequestUserMapping>) =>
      userMappingApi.updateUserMapping(mappingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_USER_MAPPING_LIST] })
    },
  })
}

export const useDeleteUserMapping = (mappingId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => userMappingApi.deleteUserMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_USER_MAPPING_LIST] })
    },
  })
}
